import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Save, Globe, Trash2, Plus, ChevronUp, ChevronDown, Star, ImagePlus, AlertTriangle, DownloadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, mediaSrc } from "@/lib/api";
import { OVERLINE, btnPrimary, btnSecondary, Panel, useAdmin } from "@/components/admin/ui";
import MediaPicker from "@/components/cms/MediaPicker";

const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 text-sm outline-none focus:border-[#1F2A22]";
const lbl = "text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]";
const BADGES = ["NEW", "SALE", "BEST SELLER"];
const money = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);

export default function AdminCommerceProductEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("marketplace");
  const [p, setP] = useState(null);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pick, setPick] = useState(null); // 'gallery' | 'homepage'

  const load = useCallback(() => { api.admCommerceProduct(id).then(setP).catch(() => toast.error("Not found")); }, [id]);
  useEffect(() => { load(); api.admCategories().then(setCats).catch(() => {}); }, [load]);

  if (!p) return <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>;

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const dollarsToCents = (v) => (v === "" || v == null ? null : Math.round(parseFloat(v) * 100));
  const centsToDollars = (c) => (c == null ? "" : (c / 100).toFixed(2));

  const save = async (silent) => {
    setBusy(true);
    try {
      const body = { title: p.title, subtitle: p.subtitle, description: p.description, price: p.price,
        sale_price: p.sale_price, compare_at_price: p.compare_at_price, cost: p.cost, category_id: p.category_id,
        badges: p.badges, homepage: p.homepage, homepage_display_image: p.homepage_display_image, images: p.images,
        variants: p.variants, features: p.features, details: p.details, care: p.care, shipping_info: p.shipping_info,
        seo_title: p.seo_title, seo_description: p.seo_description };
      const up = await api.admCommerceUpdate(id, body);
      setP(up);
      if (!silent) toast.success("Saved");
      return up;
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const publish = async () => {
    await save(true);
    try { const up = await api.admCommercePublish(id); setP(up); toast.success("Published — now live in the store"); }
    catch (e) {
      const msg = e?.response?.data?.detail || "Publish failed";
      if (msg.includes("2 product images") && window.confirm(`${msg}\n\nPublish anyway (override)?`)) {
        try { const up = await api.admCommercePublish(id, true); setP(up); toast.success("Published (override)"); } catch { toast.error("Failed"); }
      } else toast.error(msg);
    }
  };

  // images
  const pullPrintify = async () => {
    setPulling(true);
    try {
      const res = await api.admCommercePullPrintifyImages(id);
      await load();
      if (res.added > 0) toast.success(`Added ${res.added} image${res.added === 1 ? "" : "s"} from Printify`);
      else toast.info(`No new images — Printify has ${res.available_on_printify} image${res.available_on_printify === 1 ? "" : "s"} for this product, already in your gallery`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't pull images"); }
    finally { setPulling(false); }
  };
  const addImage = (src) => set("images", [...(p.images || []), { id: crypto.randomUUID(), src, alt: p.title, variant_ids: [], order: (p.images || []).length, is_primary: (p.images || []).length === 0 }]);
  const moveImg = (i, d) => { const a = [...p.images]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; set("images", a.map((x, k) => ({ ...x, order: k }))); };
  const delImg = (i) => set("images", p.images.filter((_, k) => k !== i));
  const primaryImg = (i) => set("images", p.images.map((x, k) => ({ ...x, is_primary: k === i })));

  const profit = (p.price || 0) - (p.cost || 0);

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="commerce-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => navigate("/admin/commerce/products")} className="inline-flex items-center gap-1.5 text-sm text-[#5C6B61]"><ArrowLeft size={16} /> Products</button>
        {canEdit && (
          <div className="flex gap-2">
            <button onClick={() => save(false)} disabled={busy} className={btnSecondary} data-testid="commerce-save-btn"><Save size={15} /> Save draft</button>
            <button onClick={publish} disabled={busy} className={btnPrimary} data-testid="commerce-publish-btn"><Globe size={15} /> Publish</button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6 mt-6">
        <div className="space-y-6">
          <Panel title="Details">
            <div className="space-y-3">
              <label className="block"><span className={lbl}>Title</span><input className={inp} value={p.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="ce-title" /></label>
              <label className="block"><span className={lbl}>Subtitle</span><input className={inp} value={p.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} placeholder="e.g. Soy Wax Candle" data-testid="ce-subtitle" /></label>
              <label className="block"><span className={lbl}>Description</span><textarea rows={4} className={inp} value={p.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="ce-description" /></label>
            </div>
          </Panel>

          <Panel title={<span className="flex items-center gap-2">Images {(p.images || []).length < 2 && <span className="inline-flex items-center gap-1 text-[#8A5A44] text-xs font-normal"><AlertTriangle size={13} /> min 2 to publish</span>}</span>}
            action={canEdit && p.printify_product_id ? (
              <button onClick={pullPrintify} disabled={pulling} className="inline-flex items-center gap-1.5 text-xs border border-[#1F2A22]/25 px-2.5 py-1.5 hover:bg-[#1F2A22] hover:text-white transition-colors disabled:opacity-50" data-testid="ce-pull-printify-images">
                {pulling ? <Loader2 size={13} className="animate-spin" /> : <DownloadCloud size={13} />} Pull from Printify
              </button>
            ) : null}>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {(p.images || []).map((im, i) => (
                <div key={im.id || i} className={`relative border ${im.is_primary ? "border-[#FF6A00]" : "border-[#E5E2DC]"}`} data-testid={`ce-image-${i}`}>
                  <img src={mediaSrc(im.src)} alt={im.alt} className="w-full h-24 object-cover" />
                  <div className="absolute top-1 left-1 flex gap-0.5">
                    <button onClick={() => moveImg(i, -1)} className="bg-white/90 p-0.5" title="Left"><ChevronUp size={12} className="-rotate-90" /></button>
                    <button onClick={() => moveImg(i, 1)} className="bg-white/90 p-0.5" title="Right"><ChevronDown size={12} className="-rotate-90" /></button>
                  </div>
                  <button onClick={() => delImg(i)} className="absolute top-1 right-1 bg-white/90 text-red-600 p-0.5" title="Delete"><Trash2 size={12} /></button>
                  <button onClick={() => primaryImg(i)} className={`absolute bottom-1 left-1 p-0.5 ${im.is_primary ? "text-[#FF6A00]" : "bg-white/90 text-[#5C6B61]"}`} title="Primary"><Star size={12} className={im.is_primary ? "fill-[#FF6A00]" : ""} /></button>
                </div>
              ))}
              <button onClick={() => setPick("gallery")} className="h-24 border border-dashed border-[#C7C2B8] grid place-items-center text-[#8A8478] hover:border-[#FF6A00]" data-testid="ce-add-image"><ImagePlus size={20} /></button>
            </div>
          </Panel>

          <Panel title="Variants (from Printify)">
            {(p.variants || []).length === 0 ? <p className="text-sm text-[#8A8478]">No variants.</p> : (
              <div className="max-h-64 overflow-auto border border-[#E5E2DC]">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-[#8A8478] border-b border-[#E5E2DC]"><th className="px-2 py-1.5">Enabled</th><th className="px-2 py-1.5">Color</th><th className="px-2 py-1.5">Size</th><th className="px-2 py-1.5">Cost</th></tr></thead>
                  <tbody>
                    {p.variants.map((v, i) => (
                      <tr key={v.id} className="border-b border-[#F0EEE9]">
                        <td className="px-2 py-1"><input type="checkbox" checked={v.is_enabled !== false} onChange={(e) => set("variants", p.variants.map((x, k) => k === i ? { ...x, is_enabled: e.target.checked } : x))} /></td>
                        <td className="px-2 py-1">{v.color || "—"}</td><td className="px-2 py-1">{v.size || "—"}</td><td className="px-2 py-1 text-[#8A8478]">{money(v.cost_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Feature highlights (icons on product page)">
            {(p.features || []).map((f, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input className={inp} placeholder="Icon (e.g. leaf)" value={f.icon || ""} onChange={(e) => set("features", p.features.map((x, k) => k === i ? { ...x, icon: e.target.value } : x))} />
                <input className={inp} placeholder="Title (e.g. 100% Soy Wax)" value={f.title || ""} onChange={(e) => set("features", p.features.map((x, k) => k === i ? { ...x, title: e.target.value } : x))} />
                <button onClick={() => set("features", p.features.filter((_, k) => k !== i))} className="text-red-600 px-2">×</button>
              </div>
            ))}
            <button onClick={() => set("features", [...(p.features || []), { icon: "check", title: "", order: (p.features || []).length }])} className="text-xs text-[#FF6A00] font-semibold">+ Add feature</button>
          </Panel>

          <Panel title="Product details">
            {(p.details || []).map((d, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input className={inp} placeholder="Label (e.g. Material)" value={d.label || ""} onChange={(e) => set("details", p.details.map((x, k) => k === i ? { ...x, label: e.target.value } : x))} />
                <input className={inp} placeholder="Value" value={d.value || ""} onChange={(e) => set("details", p.details.map((x, k) => k === i ? { ...x, value: e.target.value } : x))} />
                <button onClick={() => set("details", p.details.filter((_, k) => k !== i))} className="text-red-600 px-2">×</button>
              </div>
            ))}
            <button onClick={() => set("details", [...(p.details || []), { label: "", value: "" }])} className="text-xs text-[#FF6A00] font-semibold">+ Add detail</button>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <label className="block"><span className={lbl}>Shipping & Returns</span><textarea rows={3} className={inp} value={p.shipping_info || ""} onChange={(e) => set("shipping_info", e.target.value)} /></label>
              <label className="block"><span className={lbl}>Care & Safety</span><textarea rows={3} className={inp} value={p.care || ""} onChange={(e) => set("care", e.target.value)} /></label>
            </div>
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Pricing">
            <div className="space-y-3">
              <label className="block"><span className={lbl}>Retail price ($)</span><input className={inp} type="number" step="0.01" value={centsToDollars(p.price)} onChange={(e) => set("price", dollarsToCents(e.target.value))} data-testid="ce-price" /></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className={lbl}>Sale price ($)</span><input className={inp} type="number" step="0.01" value={centsToDollars(p.sale_price)} onChange={(e) => set("sale_price", dollarsToCents(e.target.value))} /></label>
                <label className="block"><span className={lbl}>Compare-at ($)</span><input className={inp} type="number" step="0.01" value={centsToDollars(p.compare_at_price)} onChange={(e) => set("compare_at_price", dollarsToCents(e.target.value))} /></label>
              </div>
              <label className="block"><span className={lbl}>Production cost ($)</span><input className={inp} type="number" step="0.01" value={centsToDollars(p.cost)} onChange={(e) => set("cost", dollarsToCents(e.target.value))} /></label>
              <div className="bg-[#F2F0EB] p-3 text-sm flex justify-between"><span className="text-[#5C6B61]">Est. profit / unit</span><span className={`font-semibold ${profit >= 0 ? "text-[#2E7D46]" : "text-red-600"}`}>{money(profit)}</span></div>
            </div>
          </Panel>

          <Panel title="Organization">
            <label className="block mb-3"><span className={lbl}>Category</span>
              <select className={inp} value={p.category_id || ""} onChange={(e) => set("category_id", e.target.value || null)} data-testid="ce-category">
                <option value="">— None —</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <span className={lbl}>Badges</span>
            <div className="flex gap-1.5 mt-1">
              {BADGES.map((b) => {
                const on = (p.badges || []).includes(b);
                return <button key={b} onClick={() => set("badges", on ? p.badges.filter((x) => x !== b) : [...(p.badges || []), b])} className={`text-xs border px-2 py-1 ${on ? "bg-[#FF6A00] text-white border-[#FF6A00]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid={`ce-badge-${b.replace(/\s/g, "-")}`}>{b}</button>;
              })}
            </div>
          </Panel>

          <Panel title="Homepage">
            <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={!!p.homepage} onChange={(e) => set("homepage", e.target.checked)} data-testid="ce-homepage" /> Feature on homepage (max 4 shown)</label>
            <span className={lbl}>Homepage display image (optional, cinematic)</span>
            {p.homepage_display_image && <img src={mediaSrc(p.homepage_display_image)} alt="" className="h-20 mt-1 object-cover border border-[#E5E2DC]" />}
            <div className="flex gap-2 mt-1">
              <button onClick={() => setPick("homepage")} className="text-xs border border-[#1F2A22]/25 px-2.5 py-1.5 hover:bg-[#1F2A22] hover:text-white">Choose image</button>
              {p.homepage_display_image && <button onClick={() => set("homepage_display_image", null)} className="text-xs text-red-600">Remove</button>}
            </div>
          </Panel>

          <Panel title="SEO">
            <div className="space-y-3">
              <label className="block"><span className={lbl}>SEO title</span><input className={inp} value={p.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} /></label>
              <label className="block"><span className={lbl}>Meta description</span><textarea rows={2} className={inp} value={p.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} /></label>
              <p className="text-xs text-[#8A8478]">URL: /shop/{p.slug}</p>
            </div>
          </Panel>
        </div>
      </div>

      <MediaPicker open={pick === "gallery"} kind="image" onClose={() => setPick(null)} onSelect={(url) => addImage(url)} />
      <MediaPicker open={pick === "homepage"} kind="image" onClose={() => setPick(null)} onSelect={(url) => set("homepage_display_image", url)} />
    </div>
  );
}
